import { AppMode, Claims, SelectorItem, TabPanel } from '@repo/frontend-types';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';

// Data
import Config from '../../../common/Config';

// Stores
import {
  useOrganizationStore,
  useUserStore,
  useSettingsStore,
  useTwinContext,
  useFilterContext,
  useModeContext,
} from '@repo/front-end-state';

// Utils
import { extractClaims } from '@repo/front-end-utils';
import { removeProtocol } from '@repo/front-end-utils';

// Components
import { Building16Regular, Building16Filled } from '@fluentui/react-icons';
import {
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
} from '@fluentui/react-components';
import type { TabOption } from '@repo/frontend-types';
import { Scrollbar } from '@repo/design-system';
import { TLCard, TLSwitcher, LabelHeader } from '@repo/ui';

import { getOrganisationById } from '@repo/front-end-services';
interface Props {
  className: string;
}

const OrganisationSelector: React.FC<Props> = ({ className }) => {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  const { getAllOrganizations, setAuth0Organizations, clearOrganizations } = useOrganizationStore();

  const organisations = getAllOrganizations();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [auth0Claims, setAuth0Claims] = useState<Claims>();

  const [hoveredOrgIndex, setHoveredOrgIndex] = useState<number | null>(null);

  const { setAuthMetadata } = useUserStore();
  const { setAppModeWithFilterReset } = useModeContext();
  const { clearFilter } = useFilterContext();
  const { setMetrics } = useSettingsStore();
  const { setTwin, setTwinBId } = useTwinContext();

  const tabOpts = [
    {
      inactiveIcon: <Building16Regular />,
      activeIcon: <Building16Filled />,
      value: TabPanel.ORGS.toString(),
      text: 'Organisations',
    },
  ];

  const [tabPanel, setTabPanel] = useState<TabPanel>(TabPanel.ORGS);
  const [tabOptions] = useState<TabOption[]>(tabOpts);

  useEffect(() => {
    const getAccessToken = async () => {
      setAccessToken(await getAccessTokenSilently());
    };
    getAccessToken();
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    setIsLoading(true);

    // Redirect the user to the login page if they are not authenticated
    if (!isAuthenticated) {
      clearOrganizations(); // Clear organizations on logout
      navigate('/', { replace: true });
    }

    if (isAuthenticated && user && accessToken) {
      const claims = extractClaims(user, Config.auth0Namespace);
      if (claims) {
        setAuth0Claims(claims);
        const orgItems: SelectorItem[] = [];
        claims.organisations.forEach(org => {
          const auth0OrgMetadata = org.split(':');
          orgItems.push({
            icon: <Building16Regular />,
            text: auth0OrgMetadata[0],
            value: auth0OrgMetadata[1],
          });
        });
        setAuth0Organizations(orgItems);
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- zustand store actions are stable
  }, [accessToken, isAuthenticated, navigate, user]);

  const clearContext = () => {
    clearFilter(true);
    setMetrics([]);
    setTwinBId(null);
    setTwin(null);
  };

  const setOrganisation = async (organisationName: string, bID: string) => {
    if (accessToken && auth0Claims) {
      const organisation = await getOrganisationById(Config.middlewareApi, accessToken, bID);

      if (organisation) {
        clearContext();

        const newAuthMetadata = {
          orgID: organisation.id,
          orgBID: bID,
          orgLongitude: organisation.longitude,
          orgLatitude: organisation.latitude,
          organisation: organisationName,
          dgraphUrl: organisation.dgraph_url ? removeProtocol(organisation.dgraph_url) : undefined,
          dgraph_namespace: organisation.dgraph_namespace,
          envName: '',
          environment: '',
          name: user?.name,
          firstName: user?.given_name,
          lastName: user?.family_name,
          email: user?.email,
          tokens: { accessToken: accessToken },
          tsdbUrl: organisation.tsdb_url,
        };
        setAuthMetadata(newAuthMetadata);
        setAppModeWithFilterReset(AppMode.TWIN_SELECTION);
      } else {
        console.error('Failed to fetch organisation data for bID:', bID);
      }
    }
  };

  const handleSelectOrganisation = async (organisationName: string, bID: string) => {
    await setOrganisation(organisationName, bID);
  };

  return (
    <div className={className}>
      <div className="header">
        <TLCard size="small">
          <TLSwitcher
            size="small"
            selectedValue={tabPanel.toString()}
            options={tabOptions}
            setValue={setTabPanel}
          />
        </TLCard>
      </div>

      {tabPanel === TabPanel.ORGS && (
        <Scrollbar
          fadeOutEnabled={false}
          className="scrollable-container"
          maxHeight={'calc(100vh - 280px)'}
        >
          <Accordion collapsible defaultOpenItems={['twins-list']}>
            <AccordionItem value="twins-list">
              <TLCard padding="0" hovered={false} selected={false}>
                <AccordionHeader expandIconPosition="end">
                  <div className="accordion-header">
                    <LabelHeader
                      id={`pick-your-org`}
                      fontSize="14px"
                      title="Pick an organisation"
                      indicator={
                        <strong>
                          {organisations?.length} org{organisations?.length === 1 ? null : 's'}
                        </strong>
                      }
                    />
                  </div>
                </AccordionHeader>
                <AccordionPanel style={{ padding: '0 8px', margin: 0 }}>
                  {isLoading ? (
                    <div className="loading">
                      <p>Loading...</p>
                    </div>
                  ) : (
                    <div className="twins-list">
                      {organisations
                        ?.sort((a, b) => a.text.localeCompare(b.text))
                        ?.map((org, i) => {
                          return (
                            <TLCard
                              key={i}
                              className="accordion-item"
                              padding="8px"
                              hovered={hoveredOrgIndex === i}
                            >
                              <div
                                className="accordion-button"
                                onClick={() => handleSelectOrganisation(org.text, org.value)}
                                onMouseEnter={() => setHoveredOrgIndex(i)}
                                onMouseLeave={() => setHoveredOrgIndex(null)}
                              >
                                <LabelHeader id={`${i}`} fontSize="12px" title={org.text} />
                              </div>
                            </TLCard>
                          );
                        })}
                    </div>
                  )}
                </AccordionPanel>
              </TLCard>
            </AccordionItem>
          </Accordion>
        </Scrollbar>
      )}
    </div>
  );
};

export default styled(OrganisationSelector)`
  .loading {
    margin: 2rem 0;
  }

  .header {
    max-width: calc(100% - 20px);
    height: 64px;
    position: sticky;
    top: 0;
  }

  .scrollable-container {
    width: 308px;
    padding-right: 20px;
  }

  .accordion-header {
    width: 100%;
    display: flex;
    .title {
      font-size: 14px;
    }
    .count {
      margin-left: auto;
    }
  }

  .accordion-item {
    margin-bottom: 12px;
  }

  .accordion-button {
    min-height: 32px;
    display: flex;
    justify-content: center;
    cursor: pointer;
  }

  .accordion-item button {
    padding: 0 !important; /* Override Fluent2 the padding on the accordion buttons */
    border: none !important;
  }
`;
